import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  getAllCapstoneProjects,
  presignCapstoneUpload,
  saveCapstoneMetadata,
  toggleCapstoneShare,
  getCapstoneDownloadUrl,
} from "../../services/capstoneProjectService";
import "./datatable.scss";

export const CapstoneProject = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingRows, setUploadingRows] = useState({});

  const fetchCapstoneProjects = async () => {
    try {
      const data = await getAllCapstoneProjects();
      setRows(data.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCapstoneProjects();
  }, []);

  const uploadProjectFile = async (studentId, file) => {
    if (!file) return;

    try {
      setUploadingRows((prev) => ({ ...prev, [studentId]: true }));

      const { uploadUrl, objectKey } = await presignCapstoneUpload(studentId, file.name);

      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });

      if (!uploadRes.ok) {
        throw new Error("Object storage upload failed");
      }

      await saveCapstoneMetadata(studentId, objectKey, file.name);

      setRows((prev) =>
        prev.map((row) =>
          row.studentId === studentId ? { ...row, originalFilename: file.name } : row
        )
      );
    } catch (err) {
      console.error("Error uploading capstone project:", err);
      alert("Upload failed");
    } finally {
      setUploadingRows((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const toggleShare = async (studentId) => {
    try {
      const res = await toggleCapstoneShare(studentId);
      const shareToStudent = res.data?.shareToStudent;
      setRows((prev) =>
        prev.map((row) => (row.studentId === studentId ? { ...row, shareToStudent } : row))
      );
    } catch (err) {
      console.error("Error toggling share status:", err);
      alert("Failed to update sharing.");
    }
  };

  const downloadProjectFile = async (studentId) => {
    try {
      const res = await getCapstoneDownloadUrl(studentId);
      window.open(res.data?.downloadUrl, "_blank");
    } catch (err) {
      console.error("Error fetching download URL:", err);
      alert("Failed to fetch download link.");
    }
  };

  const columns = [
    { field: "studentName", headerName: "Student Name", width: 200 },
    { field: "enrollmentNumber", headerName: "Enrollment Number", width: 180 },
    {
      field: "originalFilename",
      headerName: "Project File",
      width: 220,
      renderCell: (params) => params.row.originalFilename || "-",
    },
    {
      field: "upload",
      headerName: "Upload",
      width: 200,
      renderCell: (params) => {
        const { studentId, shareToStudent } = params.row;
        return (
          <input
            type="file"
            disabled={shareToStudent === true || uploadingRows[studentId]}
            onChange={(e) => uploadProjectFile(studentId, e.target.files[0])}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 200,
      renderCell: (params) => {
        const { studentId, originalFilename, shareToStudent } = params.row;
        return (
          <div className="cellAction">
            <button disabled={!originalFilename} onClick={() => toggleShare(studentId)}>
              {shareToStudent ? "Unshare" : "Share"}
            </button>
            {originalFilename && (
              <button onClick={() => downloadProjectFile(studentId)} style={{ marginLeft: 8 }}>
                Download
              </button>
            )}
          </div>
        );
      },
    },
  ];

  if (loading) return <p>Loading…</p>;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <div className="datatable">
      <DataGrid
        rows={rows}
        getRowId={(row) => row.studentId}
        columns={columns}
        pageSizeOptions={[5, 10]}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 10 } },
        }}
      />
    </div>
  );
};
